import React, { useEffect, useState } from "react"
import { SiWhatsapp } from "@icons-pack/react-simple-icons"
import { Loader2, Send } from "lucide-react"

import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import { Switch } from "src/components/ui/switch"
import { useToast } from "src/components/ui/toast"
import { useGupshupSettings } from "src/features/integrations/hooks/useGupshupSettings"

// Self-contained settings card for the Gupshup WhatsApp/SMS provider. Drops
// into the Integrations page. The API key is write-only — we show whether one
// is stored and only send a new value when the admin types one.
export default function GupshupCard() {
  const { config, isLoading, isSaving, isTesting, save, test } = useGupshupSettings()
  const { toast } = useToast()

  const [form, setForm] = useState({ app_name: "", source_number: "", enabled: false, api_key: "" })
  const [testNumber, setTestNumber] = useState("")

  // Hydrate the form once settings load (never echo the stored key back).
  useEffect(() => {
    if (!config) return
    setForm({
      app_name: config.app_name || "",
      source_number: config.source_number || "",
      enabled: !!config.enabled,
      api_key: "",
    })
  }, [config])

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }))

  const handleSave = async () => {
    try {
      // Omit api_key when left blank so we don't overwrite the stored one.
      const payload = { app_name: form.app_name, source_number: form.source_number, enabled: form.enabled }
      if (form.api_key.trim()) payload.api_key = form.api_key.trim()
      await save(payload)
      setForm((f) => ({ ...f, api_key: "" }))
      toast({ title: "Gupshup settings saved" })
    } catch (e) {
      toast({ variant: "destructive", title: "Couldn't save", description: errorMessage(e) })
    }
  }

  const handleTest = async () => {
    if (!testNumber.trim()) {
      toast({ variant: "destructive", title: "Enter a number to test" })
      return
    }
    try {
      const res = await test(testNumber.trim())
      toast({
        title: "Test accepted by Gupshup",
        description:
          res?.note ||
          `Queued to ${testNumber.trim()}. Delivery status will update on the timeline.`,
      })
    } catch (e) {
      toast({ variant: "destructive", title: "Test failed", description: errorMessage(e) })
    }
  }

  const keyPlaceholder = config?.api_key_present ? "•••••••• (saved — type to replace)" : "Enter your Gupshup API key"

  // "Configured" once the provider is enabled with credentials saved; otherwise
  // it still needs setup — mirrors the Not configured / status pill on the
  // lead-source platform cards.
  const isConfigured = !!config?.enabled && !!config?.api_key_present

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br from-green-500/10 to-green-600/5 p-6 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-green-300 hover:shadow-lg hover:shadow-slate-200/50">
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 items-center justify-center">
          <SiWhatsapp color="#25D366" size={26} />
        </div>
        <div className="min-w-0">
          <h3 className="font-semibold text-slate-900">Gupshup (WhatsApp &amp; SMS)</h3>
          <span
            className={`mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${isConfigured ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"
              }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${isConfigured ? "bg-green-500" : "bg-amber-400"}`} />
            {isConfigured ? "Configured" : "Not configured"}
          </span>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            Send WhatsApp and SMS messages to leads, with delivery status logged on the timeline.
          </p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading settings…
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3">
              <div>
                <Label htmlFor="gupshup-enabled" className="font-medium text-slate-800">Enabled</Label>
                <p className="mt-0.5 text-xs text-slate-500">
                  When off, outbound WhatsApp &amp; SMS are paused (testing still works).
                </p>
              </div>
              <Switch
                id="gupshup-enabled"
                checked={form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="gupshup-app-name" className="text-xs font-medium text-slate-600">App name</Label>
              <Input id="gupshup-app-name" value={form.app_name} onChange={set("app_name")}
                placeholder="Your Gupshup app name" />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="gupshup-source" className="text-xs font-medium text-slate-600">Source number</Label>
              <Input id="gupshup-source" value={form.source_number} onChange={set("source_number")}
                placeholder="e.g. 917834811114" />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="gupshup-key" className="text-xs font-medium text-slate-600">API key</Label>
              <Input id="gupshup-key" type="password" value={form.api_key} onChange={set("api_key")}
                placeholder={keyPlaceholder} autoComplete="off" />
            </div>

            <div className="flex items-center justify-end">
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save settings
              </Button>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <Label htmlFor="gupshup-test" className="text-xs font-medium text-slate-600">
                Send a test WhatsApp message
              </Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Input id="gupshup-test" value={testNumber} onChange={(e) => setTestNumber(e.target.value)}
                  placeholder="Destination number" />
                <Button variant="outline" onClick={handleTest} disabled={isTesting}>
                  {isTesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  <span className="ml-2">Test</span>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function errorMessage(e) {
  return e?.response?.data?.error || e?.response?.data?.errors?.[0] || "Something went wrong"
}
